/**
 * The programme editor's surfaces, and the sections under each.
 *
 * Nineteen sibling steps was the order a form is filled, not the way a
 * programme is read. The surfaces group them by what the reader is doing.
 *
 * A section keeps the legacy step keys it absorbed in `stateKeys`: those keys
 * are validation flag ids, and the `step-<key>` testids are built from them.
 *
 * @module router/programme-editor/surfaces
 */

import React from "react";

import type { Tone } from "@/components/shared/status-tones";
import type { GuidePageId } from "@/components/shell/guide-links";
import type { ModuleTab } from "@/components/studio/module-editor-model";
import { meetsReadinessGate } from "@/server/readiness-ladder";
import type { ReadinessResult } from "@/server/types/readiness";
import { lockedReason } from "@/state/step-locks";
import {
  activeSteps,
  activeViewsOutputs,
  getStepStatus,
  state,
  stepLockGate,
  steps,
  type StepStatus,
  viewsOutputs,
} from "@/state/store";
import type { Programme } from "@/types";
import { isFeAwardType } from "@/utils/award-types";
import { validateProgramme, type ValidationFlag } from "@/utils/validation";

export { lockedReason };

export type SurfaceKey = "overview" | "studio" | "submission" | "governance" | "team" | "history";

type StepComponentType = React.ComponentType<object>;

/** A legacy step component, lazily loaded. */
export type StepComponent = React.LazyExoticComponent<StepComponentType>;

export interface SectionDef {
  /** The address under its surface. Empty for the surface's index. */
  segment: string;
  title: string;
  /** The legacy step keys this section owns, in the order they stack. */
  stateKeys: readonly string[];
  /** Whether editing here contends on the programme's structural lease. */
  structural: boolean;
  /**
   * A section with no legacy step behind it, such as the matrix's coverage
   * grid. Sections that own `stateKeys` render those instead.
   */
  component?: StepComponent;
  /** The guide page about this section, where it differs from its surface's. */
  guide?: GuidePageId;
}

export interface SurfaceDef {
  key: SurfaceKey;
  segment: string;
  title: string;
  group: "primary" | "oversight";
  icon: string;
  sections: readonly SectionDef[];
  /** The guide page the top bar's Help offers on this surface. */
  guide?: GuidePageId;
}

/** What a step's module resolves to. */
type StepLoader = () => Promise<{ default: React.ComponentType<object> }>;

/**
 * Every legacy step component's loader, by its state key.
 *
 * Kept as a lookup rather than one route per step: a section renders whatever
 * `stateKeys` names, so merging two steps into one section is a change to this
 * file alone. Exported as loaders as well as components so a test can resolve
 * each one — a mistyped path otherwise fails only when a reader opens that
 * section.
 */
export const STEP_LOADERS: Readonly<Record<string, StepLoader>> = {
  identity: () =>
    import("@/components/editor/sections/identity/IdentitySection").then((m) => ({
      default: m.IdentitySection,
    })),
  "cas-picker": () =>
    import("@/components/editor/sections/identity/cas/CasSection").then((m) => ({
      default: m.CasSection,
    })),
  outcomes: () =>
    import("@/components/editor/sections/matrix/OutcomesSection").then((m) => ({
      default: m.OutcomesSection,
    })),
  versions: () =>
    import("@/components/editor/sections/delivery/DeliverySection").then((m) => ({
      default: m.DeliverySection,
    })),
  structure: () =>
    import("@/components/editor/sections/modules/ModulesInventory").then((m) => ({
      default: m.ModulesInventory,
    })),
  electives: () =>
    import("@/components/editor/sections/modules/ElectiveGroups").then((m) => ({
      default: m.ElectiveGroups,
    })),
  stages: () =>
    import("@/components/editor/sections/stages/StageStructure").then((m) => ({
      default: m.StageStructure,
    })),
  "award-pathway": () =>
    import("@/components/editor/sections/stages/AwardPathway").then((m) => ({
      default: m.AwardPathway,
    })),
  mapping: () =>
    import("@/components/editor/sections/matrix/MappingSection").then((m) => ({
      default: m.MappingSection,
    })),
  narrative: () =>
    import("@/components/editor/sections/submission/NarrativeSection").then((m) => ({
      default: m.NarrativeSection,
    })),
  schedule: () =>
    import("@/components/editor/sections/schedule/ScheduleView").then((m) => ({
      default: m.ScheduleView,
    })),
  traceability: () =>
    import("@/components/editor/sections/matrix/TraceabilitySection").then((m) => ({
      default: m.TraceabilitySection,
    })),
  snapshot: () =>
    import("@/components/editor/sections/submission/SnapshotSection").then((m) => ({
      default: m.SnapshotSection,
    })),
};

export const STEP_COMPONENTS: Readonly<Record<string, StepComponent>> = Object.fromEntries(
  Object.entries(STEP_LOADERS).map(([key, load]) => [key, React.lazy(load)]),
);

const CoverageMatrix: StepComponent = React.lazy(() =>
  import("@/components/editor/sections/matrix/CoverageMatrix").then((m) => ({
    default: m.CoverageMatrix,
  })),
);

/** The store's own title for a step key, which the step list showed. */
export function stepTitle(stateKey: string): string {
  return [...steps, ...viewsOutputs].find((step) => step.key === stateKey)?.title ?? stateKey;
}

/**
 * A section named for its first step, so its tab, heading, crumb and the
 * back/next walk all read the one title the store gives that step.
 */
function stepSection(
  segment: string,
  stateKeys: readonly [string, ...string[]],
  structural: boolean,
  guide?: GuidePageId,
): SectionDef {
  return {
    segment,
    title: stepTitle(stateKeys[0]),
    stateKeys,
    structural,
    ...(guide ? { guide } : {}),
  };
}

export const SURFACES: readonly SurfaceDef[] = [
  {
    key: "overview",
    segment: "overview",
    title: "Programme Overview",
    group: "primary",
    icon: "squares-four",
    sections: [
      stepSection("identity", ["identity", "cas-picker"], true),
      stepSection("outcomes", ["outcomes"], true),
      stepSection("delivery", ["versions"], true),
      stepSection("modules", ["structure", "electives"], true),
      stepSection("stages", ["stages", "award-pathway"], true, "award-pathways"),
      // After both the things it joins: a mapping cannot be made until there
      // are outcomes to map from and modules to map onto.
      stepSection("mapping", ["mapping"], false),
      stepSection("narrative", ["narrative"], false, "programme-narrative"),
      stepSection("schedule", ["schedule"], false),
    ],
  },
  {
    key: "studio",
    segment: "studio",
    title: "Module Studio",
    group: "primary",
    icon: "puzzle-piece",
    guide: "building-an-assigned-module",
    // One section, addressed per module rather than per legacy step: the five
    // steps became six tabs of one module editor, so the surface routes on
    // `:moduleId/:tab` and the keys below are what the flags still resolve to.
    sections: [
      {
        segment: "",
        title: "Module Studio",
        stateKeys: ["module-view", "mimlos", "effort-hours", "assessments", "reading-lists"],
        structural: false,
      },
    ],
  },
  {
    key: "submission",
    segment: "submission",
    title: "Submission pack",
    group: "primary",
    icon: "seal-check",
    guide: "readiness-and-outputs",
    // What goes to QQI, and the evidence behind it. Traceability moved here
    // from the matrix it used to sit on: it answers "can you show me", which
    // is a submission question rather than an authoring one.
    sections: [
      // No component: the index is a container, and it resolves a blocker's
      // step key through this file, which cannot import it back.
      { segment: "", title: "Readiness", stateKeys: [], structural: false },
      // Coverage leads the rest: whether every outcome is taught at all is
      // the question asked before the evidence for any one of them.
      {
        segment: "coverage",
        title: "Coverage",
        stateKeys: [],
        structural: false,
        guide: "checking-traceability",
        component: CoverageMatrix,
      },
      stepSection("traceability", ["traceability"], false, "checking-traceability"),
      stepSection("snapshot", ["snapshot"], false),
    ],
  },
  {
    key: "governance",
    segment: "governance",
    title: "Governance",
    group: "oversight",
    icon: "gavel",
    guide: "review-and-approval",
    sections: [{ segment: "", title: "Governance", stateKeys: [], structural: false }],
  },
  {
    key: "team",
    segment: "team",
    title: "Programme Team",
    group: "oversight",
    icon: "users-three",
    guide: "programme-teams",
    sections: [{ segment: "", title: "Programme Team", stateKeys: [], structural: false }],
  },
  {
    key: "history",
    segment: "history",
    title: "History",
    group: "oversight",
    icon: "clock-counter-clockwise",
    sections: [{ segment: "", title: "History", stateKeys: [], structural: false }],
  },
];

/** Where a surface opens when its own address is entered. */
export const SURFACE_INDEX: Readonly<Record<SurfaceKey, string>> = {
  overview: "identity",
  studio: "",
  submission: "",
  governance: "",
  team: "",
  history: "",
};

/** The section that owns a legacy step key, and the surface it sits on. */
export function sectionForStateKey(
  stateKey: string,
): { surface: SurfaceDef; section: SectionDef } | null {
  const surface = SURFACES.find((candidate) =>
    candidate.sections.some((s) => s.stateKeys.includes(stateKey)),
  );
  if (!surface) {
    return null;
  }
  const section = surface.sections.find((s) => s.stateKeys.includes(stateKey))!;
  return { surface, section };
}

/** The module editor tab each module step became. */
const STUDIO_TAB: Readonly<Record<string, ModuleTab>> = {
  "module-view": "details",
  mimlos: "outcomes",
  "effort-hours": "effort",
  assessments: "assessments",
  "reading-lists": "reading",
};

/**
 * The address a legacy step key now lives at.
 *
 * Every publisher of a section-navigation target names a step key, so this is
 * what keeps readiness blockers, notification deep links and the E2E specs
 * resolving after the steps stopped being addresses of their own.
 *
 * @param programmeBase - `/programmes/<id>` or the change case's own base.
 * @param stateKey - A legacy step key, e.g. `cas-picker`.
 * @param moduleId - The module a module step is about, which it then opens on.
 * @returns The path, or null when nothing owns the key.
 */
export function pathForStateKey(
  programmeBase: string,
  stateKey: string,
  moduleId?: string,
): string | null {
  const tab = STUDIO_TAB[stateKey];
  if (moduleId && tab) {
    return `${programmeBase}/studio/${encodeURIComponent(moduleId)}/${tab}`;
  }
  const found = sectionForStateKey(stateKey);
  if (!found) {
    return null;
  }
  const { surface, section } = found;
  const path = section.segment
    ? `${programmeBase}/${surface.segment}/${section.segment}`
    : `${programmeBase}/${surface.segment}`;
  // A step stacked under another on one page is a place within it, not a page.
  // The studio's keys are tabs of the module editor, not stacked steps.
  return surface.key === "studio" || section.stateKeys[0] === stateKey
    ? path
    : `${path}#step-${stateKey}`;
}

const STATUS_TONE: Readonly<Record<StepStatus, Tone>> = {
  Complete: "success",
  Review: "warning",
  Attention: "error",
  "Not started": "neutral",
};

/**
 * A section's `stateKeys` narrowed to the steps this programme has at all:
 * `cas-picker` exists only for an FE award. The mode decides what is locked,
 * not what exists, so a module editor still sees the programme's sections.
 */
export function applicableStateKeys(stateKeys: readonly string[]): string[] {
  const isFe = isFeAwardType(state.programme.awardType);
  return stateKeys.filter((key) => key !== "cas-picker" || isFe);
}

/**
 * The dot beside a surface in the sidebar: the worst status any of its
 * sections reports, so a surface never reads clean while a section under it
 * needs attention.
 *
 * `label` is what the dot says, not what the destination is called. The link
 * beside it already carries the surface's name, and naming it again here put
 * it into the link's accessible name twice.
 */
export function surfaceStatus(
  surface: SurfaceDef,
  programme: Programme,
  flags: ValidationFlag[] = validateProgramme(programme) as ValidationFlag[],
): { tone: Tone; label: string } {
  const keys = surface.sections.flatMap((section) => applicableStateKeys(section.stateKeys));
  if (keys.length === 0) {
    return { tone: "neutral", label: "" };
  }
  const order: readonly StepStatus[] = ["Attention", "Review", "Not started", "Complete"];
  const worst = keys.reduce<StepStatus>((current, key) => {
    const status = getStepStatus(key, programme, flags);
    return order.indexOf(status) < order.indexOf(current) ? status : current;
  }, "Complete");
  return { tone: STATUS_TONE[worst], label: worst };
}

/**
 * The submission pack's dot, held to the server's readiness: its steps can all
 * be complete while the pack cannot yet be exported, which the header says.
 */
export function submissionPackStatus(
  status: { tone: Tone; label: string },
  readiness: ReadinessResult | null | undefined,
): { tone: Tone; label: string } {
  if (!readiness || status.tone === "error") {
    return status;
  }
  return meetsReadinessGate(readiness.state, "Ready for final export")
    ? status
    : { tone: "warning", label: readiness.state };
}

/**
 * The step keys currently reachable, per the store's own gating. Each read
 * validates the whole programme, so a caller asking about several keys reads
 * this once and passes it.
 */
export function unlockedStateKeys(): ReadonlySet<string> {
  return new Set(
    [...activeSteps(), ...activeViewsOutputs()].filter((s) => !s.locked).map((s) => s.key),
  );
}

/** Whether a step key is currently reachable, per the store's own gating. */
export function isStateKeyUnlocked(
  stateKey: string,
  unlocked: ReadonlySet<string> = unlockedStateKeys(),
): boolean {
  return unlocked.has(stateKey);
}

export function stateKeyLockedReason(stateKey: string): string {
  if (isStateKeyUnlocked(stateKey)) {
    return "";
  }
  return lockedReason(stateKey);
}

/**
 * The step whose lock explains why a section is shut, or null when it is open.
 *
 * A section is locked when every applicable step it owns is locked; a section
 * holding one open step is open, because the reader has something to do
 * there. The identity gate takes priority over the setup gate when a section
 * mixes both, since Identity is the precondition that unblocks the rest.
 */
export function sectionLockedKey(
  section: SectionDef,
  unlocked: ReadonlySet<string> = unlockedStateKeys(),
): string | null {
  const keys = applicableStateKeys(section.stateKeys);
  if (keys.length === 0 || keys.some((key) => unlocked.has(key))) {
    return null;
  }
  const byGate = (gate: string) => keys.find((key) => stepLockGate(key) === gate);
  return byGate("identity") ?? byGate("setup") ?? keys[0]!;
}

/** Why a section cannot be opened yet, or "" when it can. */
export function sectionLockedReason(
  section: SectionDef,
  unlocked: ReadonlySet<string> = unlockedStateKeys(),
): string {
  const key = sectionLockedKey(section, unlocked);
  return key ? lockedReason(key) : "";
}
